import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AccountProductEnum,
  AccountTypeEnum,
  ExternalPlaidAccount,
  InternalAdvanceAccount,
  InternalChargeCardAccount,
  InternalCheckingAccount,
  InternalSavingsAccount,
  PillButtonRowComponent,
  SmartViewNavigationAction,
  TileType,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import DefaultAccountSummary from 'features/accounts/AccountDetails/DefaultAccountSummary';
import useGetAccountSummary from 'features/accounts/hooks/useGetAccountSummary';
import useSmartViews from 'features/accounts/hooks/useSmartViews';
import SmartViewContent from 'features/accounts/SmartViewContent/SmartViewContent';
import { SmartViewRef } from 'features/accounts/SmartViewContent/SmartViewTransactions/SmartViewTransactions';
import SmartViewTab from 'features/accounts/SmartViewTab/SmartViewTab';
import { ExternalAccountChildRef } from 'features/accounts/transactions/ExternalTransactions/ExternalTransactions';
import { ChildRef } from 'features/accounts/transactions/RecentTransactions/RecentTransactions';
import NotificationBottomSheetBanner from 'features/believe/NotificationBannerBottomSheet/NotificationBannerBottomSheet';
import { EXTERNAL_ACCOUNTS } from 'features/myVaro/constants/constants';
import { useEffect, useRef, useState } from 'react';
import {
  BackHandler,
  NativeScrollEvent,
  NativeSyntheticEvent,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';

import { getAccountAttributes } from './AccountDetails.helpers';

const AccountDetails = () => {
  const router = useRouter();
  const accountsUrl = usePathname();
  const isExternalAccount = accountsUrl?.includes(EXTERNAL_ACCOUNTS);
  const childRef = useRef<ChildRef>(null);
  const { bottom } = useSafeAreaInsets();
  const smartViewRef = useRef<SmartViewRef>(null);

  const externalChildRef = useRef<ExternalAccountChildRef>(null);

  const screenEventRef = useRef<string | undefined>(undefined);
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const { id, tab } = useLocalSearchParams();
  /**
   * launch effect to retrieve accountPageTile
   */
  const {
    accountR2,
    tiles,
    navigationTab,
    isLoading,
    refreshAccountSummary,
    staleAccountSummary,
    isInitialLoad,
  } = useGetAccountSummary(id.toString());
  const defaultCategory = navigationTab?.pills?.[0]?.onClick?.categoryId;
  const defaultSelect = tab ? tab.toString() : defaultCategory;
  const [selectedTab, setSelectedTab] = useState(defaultSelect);
  const [refreshing, setRefreshing] = useState(false);

  const isAdvanceProduct = accountR2?.product === AccountProductEnum.Advance;

  const isFirstPillSelected =
    (defaultCategory !== null &&
      defaultCategory !== undefined &&
      selectedTab === defaultCategory) ||
    selectedTab === null;

  const {
    smartViewData,
    isLoading: smartViewLoading,
    refresh,
  } = useSmartViews(id as string, selectedTab ?? '', isFirstPillSelected);

  const hasDetailLinkTile =
    tiles?.some((tile) => {
      return (
        tile?.tileType === TileType.DetailsLink ||
        tile?.tileType === TileType.InternalAccountCheckingDetailsLink
      );
    }) ||
    accountR2?.type === AccountTypeEnum.External ||
    accountR2?.product === AccountProductEnum.Advance;

  const handleSmartViewTabPress = (onClick: SmartViewNavigationAction) => {
    setSelectedTab(onClick.categoryId);
  };

  const navigateToAccountDetails = (
    accountId: string,
    productType: AccountProductEnum,
  ) => {
    const route =
      productType === AccountProductEnum.Advance
        ? '/accounts/advance-details'
        : '/accounts/manage-account';

    router.push({
      pathname: route,
      params:
        productType === AccountProductEnum.Advance
          ? { id: accountId }
          : { accountId, type: accountR2?.type },
    });
  };

  useViewDidFocusScreenAnalyticsEvent(
    screenEventRef.current
      ? {
          key: isAdvanceProduct
            ? 'advancePageKey'
            : (screenEventRef.current as string),
        }
      : null,
  );

  useEffect(() => {
    if (accountR2) {
      if (accountR2.type === AccountTypeEnum.Internal) {
        screenEventRef.current = accountR2.product;
      } else {
        screenEventRef.current = accountR2.type;
      }
    }
  }, [accountR2]);

  useEffect(() => {
    if (defaultSelect) {
      setSelectedTab(defaultSelect);
    }
  }, [defaultSelect]);

  useFocusEffect(() => {
    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => false,
    );
    return () => {
      backHandler.remove();
    };
  });

  const account = accountR2 as
    | InternalCheckingAccount
    | InternalSavingsAccount
    | ExternalPlaidAccount
    | InternalChargeCardAccount
    | InternalAdvanceAccount;

  const showDefaultSummary =
    isFirstPillSelected || !navigationTab?.id || !account;

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const isCloseToBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height - 20;

    if (account?.type === AccountTypeEnum.External) {
      externalChildRef.current?.refetchExternalTransactions();
    } else if (
      isCloseToBottom &&
      account?.product !== AccountProductEnum.Advance
    ) {
      if (!isFirstPillSelected && navigationTab?.id) {
        if (smartViewRef?.current?.pagingToken) {
          smartViewRef.current?.SmartViewTransactions();
        }
      } else if (!childRef.current?.loadingMoreData) {
        childRef.current?.fetchData();
      }
    }
  };

  const fetchSmartViewTransactions = (category: string) => {
    if (smartViewRef?.current) {
      smartViewRef.current.SmartViewTransactions(category);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    refreshAccountSummary();
    childRef.current?.fetchData();
    smartViewRef?.current?.SmartViewTransactions();
    refresh();
    setRefreshing(false);
  };

  if (isInitialLoad && isLoading) {
    return (
      <View style={styles.balanceContainer}>
        <LoadingSpinner />
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: accountR2?.nickname as string,
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => {
            return (
              <ExpoNavigationBar
                trailingAccessories={
                  hasDetailLinkTile ? (
                    <IconButton
                      iconKey="SETTINGS"
                      iconSize="MD"
                      iconButtonAccessibilityLabel="SETTINGS"
                      analyticsId={
                        isAdvanceProduct
                          ? 'manageAccountClicked'
                          : 'manageAccountIcon'
                      }
                      customAttributes={getAccountAttributes(
                        selectedTab as string,
                        accountR2?.product as AccountProductEnum,
                      )}
                      iconOnPress={() => {
                        if (isExternalAccount) {
                          router.push({
                            pathname: '/myvaro/external-accounts/[id]/details',
                            params: { id: id as string },
                          });
                        } else {
                          const accountId = Array.isArray(id) ? id?.[0] : id;
                          if (
                            accountId &&
                            accountId !== '0' &&
                            accountR2?.product
                          ) {
                            navigateToAccountDetails(
                              accountId,
                              accountR2.product,
                            );
                          }
                        }
                      }}
                    />
                  ) : null
                }
                {...props}
              />
            );
          },
        }}
      />
      {(navigationTab as PillButtonRowComponent)?.pills ? (
        <SmartViewTab
          navigationTab={navigationTab as PillButtonRowComponent}
          selectedTab={selectedTab}
          handleSmartViewTabPress={handleSmartViewTabPress}
          eventPropsData={{
            accountType: accountR2?.product,
            accountName: accountR2?.name,
          }}
        />
      ) : null}
      <ScrollView
        contentContainerStyle={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={styles.main}>
          {showDefaultSummary ? (
            <DefaultAccountSummary
              account={account}
              tiles={tiles}
              dataRef={childRef}
              externalDataRef={externalChildRef}
              staleAccountSummary={staleAccountSummary}
              selectedTab={selectedTab as string}
              bottomSheetRef={bottomSheetRef}
              refreshAccountSummary={refreshAccountSummary}
            />
          ) : (
            <SmartViewContent
              smartViewData={smartViewData}
              smartViewDataLoading={smartViewLoading}
              dataRef={smartViewRef}
              accountId={account.id}
              fetchSmartViewTransactions={fetchSmartViewTransactions}
              accountType={account.product}
              selectedTab={selectedTab as string}
            />
          )}
        </View>
      </ScrollView>
      <NotificationBottomSheetBanner
        id={account?.id}
        bottomSheetRef={bottomSheetRef}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: 4,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  main: {
    flex: 1,
    width: '100%',
  },
  scrollView: { flexDirection: 'column', gap: theme.spacing.s },
});

export default AccountDetails;
